import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { TIER_PRICING, type Tier } from '@vcard/shared';
import { useAppTranslation } from '../hooks/useAppTranslation.js';

interface UpgradePromptProps {
  /** Short sentence about what is locked; defaults to a generic text. */
  message?: string;
  /** Lowest plan that includes the feature */
  requiredTier?: Tier | undefined;
  compact?: boolean;
}

/** Lock card with a link to the plans on the billing page. Used wherever a feature is behind the paywall. */
export function UpgradePrompt({ message, requiredTier, compact = false }: UpgradePromptProps) {
  const { t } = useAppTranslation();
  const tierLabel = requiredTier ? TIER_PRICING[requiredTier].label : '';
  return (
    <div
      role="note"
      className={`flex items-start gap-3 rounded-xl border border-dashed border-line-strong bg-panel ${compact ? 'p-3' : 'p-5'}`}
    >
      <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-accent-100 dark:bg-accent-500/15 text-accent-700 dark:text-accent-300">
        <Lock className="h-4 w-4" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-ink">
          {message ?? t('paywall.upgradeTitle', 'Mit einem höheren Plan verfügbar')}
        </p>
        {tierLabel && (
          <p className="mt-0.5 text-xs text-ink-muted">{t('paywall.fromTier', 'Ab dem Plan {{tier}}', { tier: tierLabel })}</p>
        )}
        <Link
          to="/billing"
          className="mt-3 inline-flex rounded-full bg-accent-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-accent-500 transition-colors"
        >
          {t('paywall.viewPlans', 'Pläne ansehen')}
        </Link>
      </div>
    </div>
  );
}
